<script lang="ts" setup>
    import type { TrainingCourseDetail } from '~/types/academy/baseTraining';
    import { sanitizeHtml } from '~/utils/sanitizeHtml';

    defineProps<{
        isLoading?: boolean;
        training?: TrainingCourseDetail;
    }>();
</script>

<template>
    <div
        v-if="isLoading || training"
        class="academy-training-content-grid"
    >
        <template v-if="isLoading">
            <div
                class="text loading-text"
                aria-hidden="true"
            >
                <div class="loading-skeleton --slim-margin --line"></div>
                <div class="loading-skeleton --slim-margin --line"></div>
                <div class="loading-skeleton --slim-margin --line --short"></div>
                <div class="loading-skeleton --slim-margin --line"></div>
                <div class="loading-skeleton --slim-margin --line --medium"></div>
            </div>
            <div class="card-container">
                <aside
                    class="loading-card"
                    aria-hidden="true"
                >
                    <div class="loading-skeleton --slim-margin --subheadline"></div>
                    <div class="loading-skeleton --slim-margin --line"></div>
                    <div class="loading-skeleton --slim-margin --line --medium"></div>
                    <div class="loading-skeleton --slim-margin loading-button"></div>
                </aside>
            </div>
        </template>

        <template v-else-if="training">
            <!-- eslint-disable vue/no-v-html -->
            <div
                class="text"
                v-html="sanitizeHtml(training.description)"
            ></div>
            <!-- eslint-enable vue/no-v-html -->
            <div class="card-container">
                <AcademyTrainingCtaCard :training="training" />
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/basics/loading-skeleton.scss';
    @import '~/assets/scss/text/text.scss';

    .academy-training-content-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: var(--spacing);

        .card-container {
            grid-column: 1;
            grid-row: 1;
            margin-top: calc(-1 * var(--spacing));
        }

        .text {
            grid-column: 1;
            grid-row: 2;
        }

        @media (min-width: $br-m) {
            grid-template-columns: 4fr 2fr;

            .text {
                grid-column: 1;
                grid-row: 1;
            }

            .card-container {
                grid-column: 2;
                grid-row: 1;
                margin-top: inherit;
            }
        }
    }

    .loading-card {
        position: relative;
        align-self: start;
        background: var(--white);
        border-radius: var(--border-radius-s);
        box-shadow: 0 4px 10px color-mix(in srgb, var(--black) 25%, transparent);
        display: flex;
        flex-direction: column;
        gap: var(--spacing);
        padding: var(--spacing--2) var(--spacing);
        width: 100%;
    }

    .loading-skeleton.--short {
        width: 55%;
    }

    .loading-skeleton.--medium {
        width: 70%;
    }

    .loading-button {
        height: 2.5rem;
        margin-bottom: 0;
        width: 100%;
    }
</style>
